/**
 * RETOBLOCK (RETOLOGIX STUDIO V1.0) MAIN APPLICATION CONTROLLER
 * Điều phối toàn bộ các phân hệ: Workspace, BLE, Biên dịch khối lệnh, Giám sát và Quản lý tệp .reto.
 */

import confetti from 'canvas-confetti';
import { ProjectManager } from './core/project-manager.js';
import { BleTransportManager } from './core/ble-transport.js';

import { TopNavbar } from './ui/top-navbar.js';
import { SetupWizard } from './ui/setup-wizard.js';
import { BlocklyWorkspace } from './blockly/blockly-workspace.js';
import { PythonEditor } from './ui/python-editor.js';
import { TerminalDrawer } from './ui/terminal-drawer.js';
import { MonitorDashboard } from './ui/monitor-dashboard.js';
import { BleModal } from './ui/ble-modal.js';
import { OtaModal } from './ui/ota-modal.js';
import { PythonViewModal } from './ui/python-view-modal.js';
import { ListInitModal } from './ui/list-init-modal.js';
import { CloudProjectsModal } from './ui/cloud-projects-modal.js';
import { PythonGuideDrawer } from './ui/python-guide-drawer.js';
import { supabaseService } from './core/supabase-client.js';

class RetoBlockApp {
  constructor() {
    this.projectManager = new ProjectManager();
    this.bleTransport = new BleTransportManager();

    this.topNavbar = null;
    this.setupWizard = null;
    this.blocklyWorkspace = null;
    this.pythonEditor = null;
    this.terminalDrawer = null;
    this.monitorDashboard = null;
    this.pythonViewModal = null;
    this.listInitModal = null;
    this.cloudModal = null;
    this.bleModal = null;
    this.otaModal = null;
    this.pythonGuideDrawer = null;

    this.isUploading = false;
    this.isRunning = false;
    this.hasSeenRunningState = false;
    this.scriptStartTime = 0;
  }

  init() {
    // 1. Terminal Drawer
    this.terminalDrawer = new TerminalDrawer({
      container: document.getElementById('terminal-container'),
      onErrorLineClick: (lineNum) => {
        if (this.pythonEditor) {
          this.pythonEditor.highlightLine(lineNum);
        }
      }
    });

    // 2. Top Navigation Bar
    this.topNavbar = new TopNavbar({
      container: document.getElementById('top-navbar-container'),
      projectManager: this.projectManager,
      bleTransport: this.bleTransport,
      onRun: () => this.handleRunCode(),
      onStop: () => this.handleEmergencyStop(),
      onOpenMonitor: () => this.monitorDashboard.toggle(),
      onOpenPythonView: () => this.pythonViewModal.toggle(),
      onOpenBleModal: () => this.bleModal.show(),
      onNewProject: () => this.handleNewProjectPrompt(),
      onOpenProject: (file) => this.handleOpenProject(file),
      onSaveProject: () => this.handleSaveProject(),
      onOpenCloud: () => this.cloudModal.show(),
      onOpenOta: () => this.handleOpenOtaModal(),
      onOpenPythonGuide: () => this.pythonGuideDrawer?.toggle()
    });

    // 2.1. Python Guide Drawer (BLE-Safe In-App slide-out)
    this.pythonGuideDrawer = new PythonGuideDrawer({
      container: document.getElementById('python-guide-drawer-container')
    });

    // Phím tắt F1 mở Hướng dẫn lập trình Python
    window.addEventListener('keydown', (e) => {
      if (e.key === 'F1') {
        e.preventDefault();
        this.pythonGuideDrawer?.toggle();
      }
    });

    // 3. Setup Wizard Modal (Welcome & Project Launcher)
    this.setupWizard = new SetupWizard({
      container: document.getElementById('wizard-container'),
      onCreateProject: (cfg) => this.handleCreateProject(cfg),
      onOpenProject: (file) => this.handleOpenProject(file)
    });

    // 4. Sensor Dashboard Modal (Buồng Lái)
    this.monitorDashboard = new MonitorDashboard({
      container: document.getElementById('monitor-container'),
      bleTransport: this.bleTransport,
      onOpenOta: () => {
        if (this.otaModal) {
          this.otaModal.show();
        }
      }
    });

    // 5. Python View Modal
    this.pythonViewModal = new PythonViewModal({
      container: document.getElementById('python-view-container'),
      onGetCode: () => {
        if (this.blocklyWorkspace) {
          return this.blocklyWorkspace.getPythonCode();
        }
        return '';
      }
    });

    // 5. BLE Modal
    this.bleModal = new BleModal({
      container: document.getElementById('ble-modal-container'),
      bleTransport: this.bleTransport,
      onConnected: (dev) => {
        this.showToast(`Đã kết nối với ${dev.name}`, 'success');
      }
    });

    // 6. BLE Fast OTA Modal
    this.otaModal = new OtaModal({
      container: document.getElementById('ota-modal-container'),
      bleTransport: this.bleTransport,
      onCompleted: (ver) => {
        const targetVer = ver || 'v1.1.3';
        this.showToast('Nâng cấp Firmware thành công! Robot đã khởi động lại.', 'success');
        this.terminalDrawer.log(`[OTA SUCCESS] Robot đã được nâng cấp lên Firmware ${targetVer} thành công.`, 'success');
      }
    });


    // 8. List Initializer Modal (Thiết lập giá trị ban đầu cho các danh sách)
    this.listInitModal = new ListInitModal({
      container: document.getElementById('list-init-modal-container'),
      getWorkspace: () => (this.blocklyWorkspace ? this.blocklyWorkspace.workspace : null),
      onSave: (listData) => {
        this.projectManager.setDirty(true);
        this.showToast('Đã lưu thiết lập giá trị ban đầu cho các danh sách!', 'success');
        if (this.pythonEditor) {
          // If in Python mode or updating code
        }
      },
      onOpenCreateList: () => {
        if (this.blocklyWorkspace && this.blocklyWorkspace.variableModal) {
          this.blocklyWorkspace.variableModal.show({
            type: 'list',
            title: 'Tạo Danh Sách Mới',
            label: 'Tên danh sách mới:'
          });
        }
      }
    });

    // 9. Cloud Projects Modal (V1.1 Cloud Extension)
    this.cloudModal = new CloudProjectsModal({
      container: document.getElementById('cloud-modal-container'),
      projectManager: this.projectManager,
      onProjectLoaded: () => this.renderWorkspace(),
      onSaveCurrent: async () => this.syncWorkspaceDataToProject(),
      showToast: (msg, type) => this.showToast(msg, type)
    });

    // Supabase SSO & Cloud Auth State
    supabaseService.onAuthStateChange(async (user) => {
      let count = 0;
      if (user) {
        try {
          count = await supabaseService.getProjectsCount(user.id);
        } catch (e) {
          console.warn('[RETOBLOCK] Không thể lấy hạn mức:', e.message);
        }
      }
      if (this.topNavbar) {
        this.topNavbar.updateUserState(user, count);
      }
    });

    // 10. Bind Right Floating Action Toolbar Buttons
    const btnFloatMonitor = document.getElementById('btn-floating-monitor');
    const btnFloatPython = document.getElementById('btn-floating-python');
    const btnFloatListInit = document.getElementById('btn-floating-list-init');

    if (btnFloatMonitor) {
      btnFloatMonitor.addEventListener('click', () => this.monitorDashboard.toggle());
    }
    if (btnFloatPython) {
      btnFloatPython.addEventListener('click', () => this.pythonViewModal.toggle());
    }
    if (btnFloatListInit) {
      btnFloatListInit.addEventListener('click', () => this.listInitModal.show());
    }

    // Bind BLE Transport callbacks
    this.bleTransport.onStatusChange = (statusInfo) => {
      this.topNavbar.updateBleState(statusInfo.status, statusInfo.deviceName);
    };

    this.bleTransport.onLogMessage = (msg, type) => {
      this.terminalDrawer.log(msg, type);
    };

    this.latestTelemetry = null;
    this.bleTransport.onTelemetry = (telemetry) => {
      this.latestTelemetry = telemetry;
      this.monitorDashboard.updateTelemetry(telemetry);

      // Đồng bộ phiên bản Firmware trên robot vào OTA Modal nếu nhận được
      if (telemetry.firmwareVersion && this.otaModal) {
        this.otaModal.updateCurrentVersion(telemetry.firmwareVersion);
      }

      // Tự động mở khóa khi kịch bản chạy xong tự nhiên (State chuyển từ 1 -> 0 sau khi đã thực sự chạy)
      if (this.isRunning && !this.isUploading) {
        if (telemetry.robotState === 1) {
          this.hasSeenRunningState = true;
        } else if (telemetry.robotState === 0) {
          // Chỉ mở khóa nếu đã ghi nhận trạng thái chạy (1 -> 0) hoặc đã qua ít nhất 500ms kể từ khi nạp mã hoàn tất
          if (this.hasSeenRunningState || (Date.now() - this.scriptStartTime > 500)) {
            this.handleExecutionCompleted();
          }
        }
      }
    };

    this.bleTransport.onRuntimeError = (errorInfo) => {
      this.terminalDrawer.logRuntimeError(errorInfo);
      this.handleEmergencyStop(true);
      this.showToast(`Lỗi Runtime Dòng ${errorInfo.lineNum}: ${errorInfo.message}`, 'error');
      if (errorInfo.lineNum && errorInfo.lineNum > 0 && this.pythonEditor) {
        this.pythonEditor.highlightLine(errorInfo.lineNum);
      }
    };

    // Project Manager dirty change
    this.projectManager.onDirtyChange = (dirty) => {
      this.topNavbar.setUnsavedDot(dirty);
    };

    // Render initial workspace
    this.renderWorkspace();
    this.bindGlobalShortcuts();

    // Hiển thị cửa sổ Chào mừng / Khởi tạo dự án ngay khi ứng dụng khởi động
    this.setupWizard.show({ initialStep: 'welcome', isFirstLaunch: true });
  }

  renderWorkspace() {
    const workspaceSurface = document.getElementById('workspace-surface');
    workspaceSurface.innerHTML = '';

    const mode = this.projectManager.getLanguageMode();

    if (this.blocklyWorkspace) {
      this.blocklyWorkspace.dispose();
      this.blocklyWorkspace = null;
    }
    if (this.pythonEditor) {
      this.pythonEditor = null;
    }

    if (mode === 'scratch') {
      this.blocklyWorkspace = new BlocklyWorkspace({
        container: workspaceSurface,
        projectManager: this.projectManager,
        onBlocksChange: () => {
          this.projectManager.setDirty(true);
        }
      });
    } else {
      this.pythonEditor = new PythonEditor({
        container: workspaceSurface,
        projectManager: this.projectManager,
        onCodeChange: () => {
          this.projectManager.setDirty(true);
        }
      });
    }

    this.topNavbar.updateProjectDetails();
  }

  async handleRunCode() {
    // Nếu đang chạy kịch bản cũ hoặc đang nạp mã, tự động ngắt dừng trước khi nạp mã mới
    if (this.isRunning || this.isUploading) {
      await this.handleEmergencyStop(true);
      await new Promise((res) => setTimeout(res, 200));
    }

    // Kiểm tra kết nối BLE
    if (!this.bleTransport.isConnected) {
      this.showToast('Vui lòng kết nối robot qua BLE trước khi nạp code!', 'warning');
      this.bleModal.show();
      return;
    }

    let pythonCode = '';
    if (this.projectManager.getLanguageMode() === 'scratch' && this.blocklyWorkspace) {
      pythonCode = this.blocklyWorkspace.getPythonCode();
      this.projectManager.currentProject.pythonSource = pythonCode;
    } else {
      pythonCode = this.projectManager.getPythonCode();
    }

    if (!pythonCode || !pythonCode.trim()) {
      this.showToast('Chưa có mã nguồn để thực thi!', 'warning');
      return;
    }

    if (this.pythonEditor) {
      this.pythonEditor.clearErrorHighlight();
    }

    this.isUploading = true;
    this.isRunning = false;
    this.hasSeenRunningState = false;
    this.setLiveLock(true);
    this.topNavbar.setExecutionState('uploading', 0);
    this.setUploadProgress(10);

    try {
      await this.bleTransport.sendPythonScript(pythonCode, (progress) => {
        this.topNavbar.setExecutionState('uploading', progress);
        this.setUploadProgress(progress);
      });

      // Nạp mã thành công -> kích hoạt trạng thái RUNNING
      this.isUploading = false;
      this.isRunning = true;
      this.hasSeenRunningState = false;
      this.scriptStartTime = Date.now();
      this.topNavbar.setExecutionState('running');
      this.setUploadProgress(0);
      this.terminalDrawer.log('[LIVE MODE] Robot đang thực thi kịch bản trên bo mạch...', 'success');
    } catch (err) {
      this.isUploading = false;
      this.isRunning = false;
      this.hasSeenRunningState = false;
      this.bleTransport.abortUpload();
      this.setLiveLock(false);
      this.topNavbar.setExecutionState('idle');
      this.setUploadProgress(0);
      this.showToast(`Lỗi nạp mã: ${err.message}`, 'error');
    }
  }

  async handleEmergencyStop(isError = false) {
    this.isUploading = false;
    this.isRunning = false;
    this.hasSeenRunningState = false;
    this.bleTransport.abortUpload();
    this.terminalDrawer.log('Kích hoạt ngắt khẩn cấp STOP!', 'error');
    try {
      await this.bleTransport.sendEmergencyStop();
    } catch (e) {}

    this.setLiveLock(false);
    this.topNavbar.setExecutionState('idle');
    this.setUploadProgress(0);

    if (!isError) {
      this.showToast('Đã dừng xe khẩn cấp và mở khóa vùng làm việc', 'info');
    }
  }

  handleExecutionCompleted() {
    this.isUploading = false;
    this.isRunning = false;
    this.hasSeenRunningState = false;
    this.setLiveLock(false);
    this.topNavbar.setExecutionState('idle');
    this.setUploadProgress(0);
    this.terminalDrawer.log('Kịch bản hoàn tất thành công! Mở khóa vùng soạn thảo.', 'success');
    this.showToast('Chương trình đã hoàn thành thành công!', 'success');

    // Hiệu ứng pháo giấy chúc mừng
    try {
      confetti({
        particleCount: 50,
        spread: 60,
        origin: { y: 0.8 }
      });
    } catch (e) {}
  }

  setLiveLock(locked) {
    const overlay = document.getElementById('live-lock-overlay');
    if (overlay) {
      if (locked) overlay.classList.add('active');
      else overlay.classList.remove('active');
    }
  }

  setUploadProgress(pct) {
    const bar = document.getElementById('upload-progress-bar');
    if (bar) {
      bar.style.width = `${pct}%`;
    }
  }

  handleNewProjectPrompt() {
    if (this.projectManager.isDirty) {
      if (!confirm('Dự án có thay đổi chưa được lưu. Bạn có muốn tạo dự án mới không?')) {
        return;
      }
    }
    this.setupWizard.show({ initialStep: 'new_project', isFirstLaunch: false });
  }

  handleCreateProject(config) {
    const targetKit = config.targetKit || 'RETOLOGIX-BASIC';
    this.projectManager.currentProject = this.projectManager.createDefaultProject(
      config.name,
      config.languageMode,
      targetKit
    );
    this.projectManager.setDirty(false);
    this.renderWorkspace();
    const kitDisplayName = (targetKit === 'RETOLOGIX-BASIC' || targetKit === 'RETOCAR-STARTER') ? 'RETOLOGIX Basic' : targetKit;
    this.showToast(`Đã tạo dự án mới: ${config.name} (${kitDisplayName} - ${config.languageMode.toUpperCase()})`, 'success');
  }

  async handleOpenProject(file) {
    try {
      this.terminalDrawer.log(`Đang đọc tệp ${file.name}...`, 'info');
      await this.projectManager.importRetoFile(file);
      this.renderWorkspace();
      this.showToast(`Đã mở dự án: ${this.projectManager.getProjectName()}`, 'success');
    } catch (err) {
      this.showToast(`Lỗi mở tệp: ${err.message}`, 'error');
      this.terminalDrawer.log(`Lỗi đọc file .reto: ${err.message}`, 'error');
    }
  }

  syncWorkspaceDataToProject() {
    if (this.blocklyWorkspace && this.projectManager.getLanguageMode() === 'scratch') {
      const blocksData = this.blocklyWorkspace.saveBlocksData();
      this.projectManager.updateBlocksData(blocksData);
      this.projectManager.currentProject.pythonSource = this.blocklyWorkspace.getPythonCode();
    } else if (this.pythonEditor && this.projectManager.getLanguageMode() === 'python') {
      this.projectManager.updatePythonSource(this.pythonEditor.getCode());
    }
  }

  async handleSaveProject() {
    try {
      this.syncWorkspaceDataToProject();
      const filename = await this.projectManager.exportRetoFile();
      this.showToast(`Đã lưu dự án: ${filename}`, 'success');
      this.terminalDrawer.log(`Lưu tệp ${filename} thành công.`, 'success');
    } catch (err) {
      this.showToast(`Lỗi lưu tệp: ${err.message}`, 'error');
    }
  }

  handleOpenOtaModal() {
    if (!this.bleTransport.isConnected) {
      this.showToast('Vui lòng kết nối robot qua BLE trước khi nâng cấp firmware!', 'warning');
      this.bleModal.show();
      return;
    }
    if (this.isRunning || this.isUploading) {
      this.showToast('Vui lòng dừng kịch bản đang chạy trước khi nâng cấp firmware!', 'warning');
      return;
    }
    this.otaModal.show();
  }

  bindGlobalShortcuts() {
    window.addEventListener('keydown', (e) => {
      // Ctrl+S: Lưu tệp
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        this.handleSaveProject();
      }
      // Ctrl+O: Mở tệp
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'o') {
        e.preventDefault();
        document.getElementById('reto-file-input').click();
      }
      // Ctrl+N: Dự án mới
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'n') {
        e.preventDefault();
        this.handleNewProjectPrompt();
      }
      // F5: Chạy kịch bản
      if (e.key === 'F5') {
        e.preventDefault();
        this.handleRunCode();
      }
      // Escape: Dừng khẩn cấp
      if (e.key === 'Escape') {
        if (this.isRunning || this.isUploading) {
          e.preventDefault();
          this.handleEmergencyStop();
        }
      }
    });

    // Cảnh báo khi tắt trình duyệt mà chưa lưu
    window.addEventListener('beforeunload', (e) => {
      if (this.projectManager.isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    });
  }

  showToast(message, type = 'info') {
    const container = document.getElementById('toast-container');
    if (!container) return;

    const toast = document.createElement('div');
    toast.className = `toast ${type}`;
    toast.textContent = message;
    container.appendChild(toast);

    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateX(20px)';
      toast.style.transition = 'all 0.2s';
      setTimeout(() => toast.remove(), 200);
    }, 3500);
  }
}

// Khởi chạy ứng dụng khi DOM sẵn sàng
window.addEventListener('DOMContentLoaded', () => {
  const app = new RetoBlockApp();
  app.init();
  window.retoblockApp = app;
});
